import React, { useMemo, useCallback, useContext } from 'react';
import { 
  ExclamationTriangleIcon, 
  UserGroupIcon, 
  CurrencyDollarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { ThemeContext } from '../context/ThemeContext';

// Componente memoizado para las tarjetas individuales
const StatCard = React.memo(({ card, onClick, modoOscuro, inPanel = false }) => {
  const handleClick = useCallback(() => {
    onClick(card.id);
  }, [onClick, card.id]);

  const cardBox = inPanel
    ? (modoOscuro ? 'bg-gray-800/70 border-gray-700/60 hover:bg-gray-800' : 'bg-gray-50 border-gray-200 hover:bg-white')
    : (modoOscuro 
      ? 'bg-gray-900/80 border-gray-700/50 hover:bg-gray-800/80' 
      : 'bg-white border-gray-100 shadow-sm hover:shadow-md');
  const iconBg = modoOscuro ? 'bg-[#1F80FF]/20' : 'bg-[#1F80FF]/10';
  const iconColor = '#1F80FF';
  const titleColor = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const valueColor = modoOscuro ? 'text-white' : 'text-gray-900';
  const subtitleColor = modoOscuro ? 'text-gray-500' : 'text-gray-400';

  return (
    <div
      onClick={handleClick}
      className={`rounded-xl p-3.5 sm:p-4 border transition-all duration-200 cursor-pointer min-w-0 overflow-hidden ${cardBox}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
          <card.icon className="h-5 w-5" style={{ color: iconColor }} />
        </div>
        {card.trend && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            card.trend > 0 
              ? 'bg-green-500/10 text-green-500' 
              : card.trend < 0 
                ? 'bg-red-500/10 text-red-500' 
                : 'bg-gray-500/10 text-gray-500'
          }`}>
            {card.trend > 0 ? '+' : ''}{card.trend}%
          </span>
        )}
      </div>
      
      <div className="space-y-1 min-w-0">
        <div className={`text-xs font-medium uppercase tracking-wide ${titleColor}`}>
          {card.title}
        </div>
        <div className={`text-[15px] sm:text-xl lg:text-2xl font-bold tabular-nums leading-tight break-words [overflow-wrap:anywhere] ${valueColor}`}>
          {card.value}
        </div>
        <div className={`text-[11px] leading-snug break-words [overflow-wrap:anywhere] ${subtitleColor}`}>
          {card.subtitle}
        </div>
      </div>
    </div>
  );
});

StatCard.displayName = 'StatCard';

const DashboardStats = React.memo(({ stats, loading, onCardClick, inPanel = false, vista = 'consolidado', currency = 'ARS' }) => {
  const { modoOscuro } = useContext(ThemeContext);
  // Formateo de montos según moneda
  const formatCurrency = useCallback((amount, currency = 'ARS') => {
    if (!currency) currency = 'ARS';
    try {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency
      }).format(amount || 0);
    } catch {
      // Fallback simple si la moneda no es válida
      return `${currency} ${(amount || 0).toLocaleString('es-AR')}`;
    }
  }, []);

  // Memoización de las tarjetas de estadísticas para evitar re-creaciones
  const statCards = useMemo(() => {
    const usedDocs = stats.resumen?.documentosMesUsuario || 0;
    const extraDocs = stats.resumen?.extraDocuments || 0;
    const unlimited = stats.resumen?.planUnlimited;
    const maxDocs = unlimited
      ? null
      : (stats.resumen?.effectiveLimit ?? stats.resumen?.maxDocumentsPerMonth ?? null);
    const usagePercent = stats.resumen?.planUsagePercent ?? (maxDocs ? Math.round((usedDocs / maxDocs) * 100) : null);

    const ingresosPorMoneda = stats.resumen?.totalesPorMoneda || [];
    const impagasPorMoneda = stats.impagas?.totalesPorMoneda || [];

    const ingresosValue = formatCurrency(stats.resumen?.totalIngresos || 0, currency);
    let ingresosSubtitle = `${stats.resumen?.facturasMes || 0} registros del mes`;
    if (vista === 'consolidado' && ingresosPorMoneda.length > 1) {
      ingresosSubtitle = ingresosPorMoneda
        .map((t) => `${t.moneda || '—'} ${Number(t.total || 0).toLocaleString('es-AR')}`)
        .join(' · ');
    } else if (vista !== 'consolidado') {
      ingresosSubtitle = `${vista} · ${stats.resumen?.facturasMes || 0} registros del mes`;
    }

    let impagasSubtitle = formatCurrency(stats.impagas?.monto || 0, currency);
    if (vista === 'consolidado' && impagasPorMoneda.length > 1) {
      impagasSubtitle = impagasPorMoneda
        .map((t) => `${t.moneda || '—'} ${Number(t.total || 0).toLocaleString('es-AR')}`)
        .join(' · ');
    }

    return [
      {
        id: 'impagas',
        title: 'Registros impagos',
        value: stats.impagas?.cantidad || 0,
        subtitle: impagasSubtitle,
        icon: ExclamationTriangleIcon
      },
      {
        id: 'clientes',
        title: 'Clientes únicos',
        value: stats.resumen?.clientesUnicos || 0,
        subtitle: `${stats.resumen?.totalFacturas || 0} registros totales`,
        icon: UserGroupIcon
      },
      {
        id: 'ingresos',
        title: 'Ingresos totales',
        value: ingresosValue,
        subtitle: ingresosSubtitle,
        icon: CurrencyDollarIcon
      },
      {
        id: 'uso-plan',
        title: 'Uso de plan',
        value: maxDocs ? `${usedDocs} / ${maxDocs}` : usedDocs,
        subtitle: unlimited
          ? 'Sin límite mensual'
          : maxDocs && usagePercent !== null
            ? `${usagePercent}% del límite${extraDocs > 0 ? ` (+${extraDocs} extra)` : ''}`
            : 'Volumen mensual de registros',
        icon: DocumentTextIcon
      }
    ];
  }, [stats, formatCurrency, vista, currency]);

  // Memoización del handler de click
  const handleCardClick = useCallback((cardId) => {
    onCardClick(cardId);
  }, [onCardClick]);

  // Estado cuando no hay datos
  const hasData = stats.resumen?.totalFacturas > 0 || stats.resumen?.totalIngresos > 0;
  
  if (!hasData && !loading) {
    return (
      <div className="mb-6">
        <div className={`rounded-2xl p-6 border text-center ${
          modoOscuro ? 'bg-gray-900/80 border-gray-700/50' : 'bg-white border-gray-100 shadow-sm'
        }`}>
          <h3 className={`text-base font-medium mb-1 ${modoOscuro ? 'text-white' : 'text-gray-900'}`}>Sin datos disponibles</h3>
          <p className={`text-sm mb-4 ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
            Registrá tus primeras ventas para ver métricas.
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            <button
              onClick={() => window.location.href = '/facturas?tab=crear&modo=rapido'}
              className="px-4 py-2 bg-[#1F80FF] text-white rounded-lg hover:bg-[#004AAD] transition-colors text-sm"
            >
              Registrar venta rápida
            </button>
            <button
              onClick={() => window.location.href = '/uso-plan'}
              className={`px-4 py-2 rounded-lg transition-colors text-sm border ${
                modoOscuro ? 'border-gray-600 text-white hover:bg-gray-800' : 'border-gray-300 text-gray-800 hover:bg-gray-50'
              }`}
            >
              Uso del plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Verificar si los datos parecen reales o son placeholder
  const hasRealData = stats.resumen?.totalFacturas > 0 || 
                     stats.resumen?.totalIngresos > 0 || 
                     stats.impagas?.cantidad > 0;
  
  // Mostrar advertencia si no hay datos reales pero se muestran valores
  if (!hasRealData && !loading) {
    return (
      <div className="mb-6">
        <div className={`rounded-2xl p-4 border ${
          modoOscuro ? 'bg-gray-900/80 border-gray-700/50' : 'bg-white border-gray-100 shadow-sm'
        }`}>
          <p className={`text-sm ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
            No se encontraron registros de venta. Registrá tus primeras ventas para ver métricas reales.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 ${inPanel ? 'mb-0' : 'mb-4 sm:mb-6'}`}>
      {statCards.map((card) => (
        <StatCard 
          key={card.id} 
          card={card} 
          onClick={handleCardClick}
          modoOscuro={modoOscuro}
          inPanel={inPanel}
        />
      ))}
    </div>
  );
});

DashboardStats.displayName = 'DashboardStats';

export default DashboardStats; 